import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, ResponsiveContainer, Legend } from "recharts";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import ShareBar from "@/components/ShareBar";
import MarkdownToolbar from "@/components/MarkdownToolbar";
import { renderInlineLinks } from "@/lib/inline";
import SEO, { makeMetaDescription, SITE_ORIGIN, SITE_NAME } from "@/components/SEO";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

// Convert an alignment value (persisted on the post document) into a tailwind class.
// Any unknown value falls back to left-aligned so old posts continue to look identical.
const alignCls = (v) => (v === "center" ? "text-center" : "");

// Render a paragraph — supports:
//   - image blocks:   ![alt](url)                      centered
//                     ![alt|left](url)                 left-aligned block
//                     ![alt|right](url)                right-aligned block
//   - inline images:  ![alt|left|wrap](url)            floated left, text wraps
//                     ![alt|right|wrap](url)           floated right, text wraps
//   - inline links:   [text](https://url) or [text](/path)
//   - italic text:    *italic text*
const BLOCK_IMG_RE = /^!\[([^|\]]*)(?:\|(left|center|right))?\]\((\S+)\)$/;
const INLINE_TOKEN_RE = /!\[([^|\]]*)(?:\|(left|right))\|wrap\]\((\S+?)\)|\[([^\]]+)\]\((\/[^)\s]+|https?:\/\/[^)\s]+)\)|\*([^*\n]+)\*/g;

function renderParagraph(text, key, className) {
  if (!text) return null;
  const trimmed = text.trim();
  const imgMatch = trimmed.match(BLOCK_IMG_RE);
  if (imgMatch) {
    const [, alt, align, src] = imgMatch;
    const justify = align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center";
    return (
      <figure key={key} className={`my-8 flex ${justify}`}>
        <img src={src} alt={alt || ""} className="w-[57.5%] h-auto border border-neutral-200" />
      </figure>
    );
  }
  return (
    <p key={key} className={className}>
      {renderInline(text)}
    </p>
  );
}

function renderInline(text) {
  if (!text) return null;
  const parts = [];
  INLINE_TOKEN_RE.lastIndex = 0;
  let last = 0;
  let m;
  let idx = 0;
  while ((m = INLINE_TOKEN_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[3]) {
      // Wrap image: m[1]=alt, m[2]=left|right, m[3]=url
      const alt = m[1] || "";
      const align = m[2];
      const floatCls = align === "left" ? "float-left mr-6 mb-3" : "float-right ml-6 mb-3";
      parts.push(
        <img
          key={`img-${idx++}`}
          src={m[3]}
          alt={alt}
          className={`${floatCls} w-2/5 max-w-[320px] border border-neutral-200`}
        />
      );
    } else {
      // Link: m[4]=label, m[5]=href, or italic: m[6]=text
      if (m[6]) {
        parts.push(
          <em key={`em-${idx++}`} className="italic">
            {m[6]}
          </em>
        );
      } else {
        const label = m[4];
        const href = m[5];
        const isExternal = /^https?:/i.test(href);
        const linkStyle = { color: "#fc4707", textDecorationColor: "#fc4707" };
        parts.push(
          isExternal ? (
            <a
              key={`lnk-${idx++}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="edi-link hover:brightness-90"
              style={linkStyle}
            >
              {label}
            </a>
          ) : (
            <Link
              key={`lnk-${idx++}`}
              to={href}
              className="edi-link hover:brightness-90"
              style={linkStyle}
            >
              {label}
            </Link>
          )
        );
      }
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function EssayView({ post }) {
  return (
    <article className="max-w-[960px] mx-auto px-4 py-12">
      <h1 className={`serif text-4xl sm:text-6xl mt-4 tracking-tight leading-tight whitespace-pre-line ${alignCls(post.title_align)}`}>{post.title}</h1>
      {post.summary && <p className={`serif italic text-xl sm:text-2xl mt-6 text-neutral-700 leading-snug ${alignCls(post.summary_align)}`}>{post.summary}</p>}
      <div className="mt-8 flex items-center gap-3 mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 pb-8 border-b border-black">
        <span data-testid="post-author-name">By {post.author?.name}</span>
        <span className="text-neutral-400">/</span>
        <span>{formatDate(post.created_at)}</span>
      </div>
      {post.media_url && post.media_type === "video" && (
        <figure className="mt-10">
          <video src={post.media_url} controls className="w-full max-h-[75vh] bg-black" data-testid="interview-video" />
        </figure>
      )}
      {post.media_url && post.media_type === "audio" && (
        <figure className="mt-10">
          <audio src={post.media_url} controls className="w-full" data-testid="interview-audio" />
        </figure>
      )}
      {post.media_url && post.media_type === "youtube" && (
        <figure className="mt-10 aspect-video">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${post.media_url}?rel=0`}
            title={post.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-0 bg-black"
            data-testid="interview-youtube"
          />
        </figure>
      )}
      {post.media_url && post.media_type === "vimeo" && (
        <figure className="mt-10 aspect-video">
          <iframe
            src={`https://player.vimeo.com/video/${post.media_url}`}
            title={post.title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-0 bg-black"
            data-testid="interview-vimeo"
          />
        </figure>
      )}
      <div className="prose max-w-none mt-10">
        {post.body?.split("\n\n").map((para, i) =>
          renderParagraph(
            para,
            i,
            "text-lg leading-relaxed mb-6 text-neutral-900"
          )
        )}
      </div>
      <ShareBar title={post.title} />
    </article>
  );
}

function LikertPollView({ post, onVote }) {
  const { user } = useAuth();
  const authed = user && user !== false;
  const [busy, setBusy] = useState(false);
  const agg = post.likert_aggregates || [];

  const rate = async (statement_index, rating) => {
    if (!authed) { toast.error("You must sign in to vote."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/vote/likert`, { statement_index, rating });
      onVote(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const LABELS = ["Not at all", "", "Neutral", "", "Absolutely"];
  const BAR_COLORS = ["#8ed973", "#2a738f", "#a02b93"];

  return (
    <article className="max-w-[960px] mx-auto px-4 py-12">
      <h1 className={`serif text-4xl sm:text-5xl mt-4 tracking-tight whitespace-pre-line ${alignCls(post.title_align)}`}>{post.title}</h1>
      {post.summary && <p className={`mt-6 text-neutral-700 text-lg leading-relaxed ${alignCls(post.summary_align)}`}>{post.summary}</p>}
      <div className="mt-6 pb-6 border-b border-black" />

      <div className="mt-10 mono text-xs uppercase tracking-[0.25em] text-center" style={{ color: "#fc4707" }}>
        1 = Not at all &middot; 5 = Absolutely
      </div>

      <div className="mt-4 space-y-10">
        {agg.map((row, idx) => (
          <div key={idx} data-testid={`likert-row-${idx}`}>
            <div className="serif text-xl">{row.statement}</div>
            <div className="mt-3 grid grid-cols-5 gap-2">
              {[1,2,3,4,5].map((r) => {
                const selected = row.my_rating === r;
                const locked = row.my_rating != null;
                return (
                  <button
                    key={r}
                    disabled={busy || locked}
                    onClick={() => rate(idx, r)}
                    data-testid={`likert-${idx}-${r}`}
                    className={`py-3 border mono text-sm transition-colors ${selected ? "bg-amber-600 text-white border-amber-700" : "border-neutral-300 hover:border-black"} ${locked && !selected ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
            <div className="mt-1 grid grid-cols-5 gap-2 mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">
              {LABELS.map((l, i) => (<div key={i} className="text-center">{l}</div>))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-14 border-t border-black pt-8">
        <div className="mono text-xs uppercase tracking-[0.3em] text-amber-700 mb-4">Respondents feeling less than neutral (rating 1 or 2)</div>
        <div className="space-y-4">
          {agg.map((row, idx) => (
            <div key={idx}>
              <div className="flex justify-between mono text-xs uppercase tracking-[0.15em] mb-1">
                <span className="truncate max-w-[70%]">{row.statement}</span>
                <span className="tabular-nums">{row.low_pct}% <span className="text-neutral-500">({row.total} vote{row.total===1?"":"s"})</span></span>
              </div>
              <div className="h-3 bg-neutral-100 border border-neutral-200">
                <div className="h-full poll-bar" style={{ width: `${row.low_pct}%`, background: BAR_COLORS[idx] || "#d97706" }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {!authed && (
        <p className="mt-8 mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          <Link to="/login" className="edi-link">Sign in</Link> to vote.
        </p>
      )}
      <ShareBar title={post.title} />
    </article>
  );
}

function PollChart({ post }) {
  const options = post.poll_options || [];
  const colors = post.chart_colors || [];
  const data = options.map((opt, i) => ({
    name: opt,
    votes: post.poll_counts?.[opt] || 0,
    fill: colors[i] || "#d97706",
  }));
  const type = post.chart_type || "bar";
  if (type === "pie") {
    return (
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey="votes" nameKey="name" outerRadius={110} label={(e) => `${e.name} (${e.votes})`}>
            {data.map((d, i) => (<Cell key={i} fill={d.fill} />))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (type === "line") {
    return (
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 30 }}>
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Line type="monotone" dataKey="votes" stroke={colors[0] || "#0A0A0A"} strokeWidth={2} dot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 30 }}>
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey="votes">
          {data.map((d, i) => (<Cell key={i} fill={d.fill} />))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function PollView({ post, onVote }) {
  const { user } = useAuth();
  const total = post.poll_total_votes || 0;
  const [voting, setVoting] = useState(false);
  const authed = user && user !== false;

  const handleVote = async (option) => {
    if (!authed) {
      toast.error("You must sign in to vote.");
      return;
    }
    setVoting(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/vote`, { option });
      onVote(data);
      toast.success("Vote recorded.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVoting(false);
    }
  };

  return (
    <article className="max-w-[960px] mx-auto px-4 py-12">
      <h1 className={`serif text-4xl sm:text-5xl mt-4 tracking-tight leading-tight whitespace-pre-line ${alignCls(post.title_align)}`}>{post.title}</h1>
      {post.summary && <p className={`mt-6 text-neutral-700 text-lg leading-relaxed ${alignCls(post.summary_align)}`}>{post.summary}</p>}
      <div className="mt-6 mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 pb-6 border-b border-black">
        By {post.author?.name} &middot; {formatDate(post.created_at)} &middot; {total} vote{total === 1 ? "" : "s"}
      </div>

      <div className="mt-10 space-y-4">
        {(post.poll_options || []).map((opt, i) => {
          const c = post.poll_counts?.[opt] || 0;
          const pct = total > 0 ? Math.round((c / total) * 100) : 0;
          const selected = post.user_vote === opt;
          const locked = post.user_vote != null;
          const swatch = post.chart_colors?.[i];
          return (
            <button
              key={opt}
              data-testid={`poll-option-${opt}`}
              onClick={() => handleVote(opt)}
              disabled={voting || locked}
              className={`relative w-full text-left border transition-colors ${
                selected ? "border-amber-700" : "border-neutral-300 hover:border-black"
              } ${locked && !selected ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <div
                className="absolute inset-y-0 left-0 poll-bar"
                style={{ width: `${pct}%`, background: swatch ? `${swatch}22` : (selected ? "#fef3c7" : "#f5f5f5") }}
              />
              <div className="relative flex items-baseline justify-between px-5 py-4">
                <div className="flex items-baseline gap-3">
                  {swatch && <span className="inline-block w-3 h-3 border border-neutral-300" style={{ background: swatch }} />}
                  <span className={`serif text-xl ${selected ? "text-amber-800" : ""}`}>{opt}</span>
                  {selected && <span className="mono text-[10px] uppercase tracking-[0.25em] text-amber-700">Your pick</span>}
                </div>
                <div className="mono text-sm tabular-nums">
                  <span className="text-neutral-900 font-semibold">{pct}%</span>
                  <span className="text-neutral-500 ml-3">{c}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {total > 0 && post.chart_type && (
        <div className="mt-12 border-t border-black pt-6">
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-700 mb-4">Results chart</div>
          <div style={{ width: "100%", height: 320 }}>
            <PollChart post={post} />
          </div>
        </div>
      )}

      {!authed && (
        <p className="mt-8 mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          <Link to="/login" className="edi-link">Sign in</Link> to vote.
        </p>
      )}
      <ShareBar title={post.title} />
    </article>
  );
}

function ForumView({ post, comments, onCommentAdded }) {
  const { user } = useAuth();
  const authed = user && user !== false;
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const bodyRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/posts/${post.id}/comments`, { body });
      setBody("");
      toast.success("Reply submitted for editor review.");
      onCommentAdded();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article className="max-w-[960px] mx-auto px-4 py-12">
      <h1 className={`serif text-4xl sm:text-5xl mt-4 tracking-tight leading-tight whitespace-pre-line ${alignCls(post.title_align)}`}>{post.title}</h1>
      {post.summary && <p className={`mt-6 text-neutral-700 text-lg leading-relaxed ${alignCls(post.summary_align)}`}>{post.summary}</p>}
      <div className="mt-6 mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 pb-6 border-b border-black">
        Started by {post.author?.name} &middot; {formatDate(post.created_at)}
      </div>
      <div className="prose max-w-none mt-10">
        {post.body?.split("\n\n").map((para, i) =>
          renderParagraph(para, i, "text-base leading-relaxed mb-5 text-neutral-800")
        )}
      </div>

      <ShareBar title={post.title} />

      <div className="mt-16 border-t border-black pt-8">
        <div className="flex items-baseline justify-between mb-6">
          <h3 className="serif text-2xl">The Discussion</h3>
          <span className="mono text-xs uppercase tracking-[0.25em] text-neutral-500">
            {comments.length} repl{comments.length === 1 ? "y" : "ies"}
          </span>
        </div>

        {post.closed && (
          <div className="mb-8 border border-amber-700 bg-amber-50 px-4 py-3" data-testid="forum-closed-banner">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-700">Forum closed</div>
            <p className="text-sm text-neutral-800 mt-1">
              {post.closed_reason === "reached_100_replies"
                ? "This forum has reached 100 replies and is now closed. The editor is preparing a summary."
                : "The editor has closed this forum. It is no longer accepting new replies."}
            </p>
          </div>
        )}

        {authed && !post.closed ? (
          <form onSubmit={submit} className="mb-10 border border-neutral-300 p-4">
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Your reply</label>
            <textarea
              ref={bodyRef}
              data-testid="comment-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="Please have your say, leave a reply. However, all submissions are moderated, and all comments will be reviewed before being posted to the site."
              className="w-full mt-2 bg-transparent border-t border-neutral-200 pt-3 focus:outline-none text-base leading-relaxed resize-none"
            />
            <MarkdownToolbar textareaRef={bodyRef} value={body} onChange={setBody} hideLink />
            <div className="flex justify-end mt-3">
              <button
                type="submit"
                disabled={submitting || !body.trim()}
                data-testid="comment-submit-button"
                className="bg-black text-white px-5 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 transition-colors disabled:opacity-40"
              >
                {submitting ? "Submitting…" : "Submit reply"}
              </button>
            </div>
          </form>
        ) : !post.closed ? (
          <div className="mb-10 border border-dashed border-neutral-400 p-6 text-center">
            <p className="text-neutral-700">
              <Link to="/login" className="edi-link">Sign in</Link> or{" "}
              <Link to="/register" className="edi-link">join</Link> to add your voice to the discussion.
            </p>
          </div>
        ) : null}

        {comments.length === 0 ? (
          <p className="mono text-sm text-neutral-500">No replies yet. Be the first.</p>
        ) : (
          <ul className="space-y-6">
            {comments.map((c) => (
              <li key={c.id} className="border-l-2 border-neutral-300 pl-4">
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  {c.author?.name}
                  {c.author?.role === "admin" && <span className="text-amber-700"> · editor</span>}
                  <span className="text-neutral-400 mx-2">/</span>
                  {formatDate(c.created_at)}
                </div>
                <p className="mt-2 text-base leading-relaxed text-neutral-800 whitespace-pre-wrap">{renderInlineLinks(c.body)}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

export default function PostDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [error, setError] = useState(null);

  const load = () => {
    api.get(`/posts/${id}`).then((r) => setPost(r.data)).catch((e) => setError(formatApiError(e)));
  };
  const loadComments = () => {
    api.get(`/posts/${id}/comments`).then((r) => setComments(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) {
    return (
      <Layout>
        <div className="max-w-[960px] mx-auto px-4 py-24 text-center">
          <div className="mono text-xs uppercase tracking-[0.3em] text-neutral-500">404</div>
          <h1 className="serif text-5xl mt-4">Story not found</h1>
          <p className="text-neutral-600 mt-4">This piece may be pending review or was never published.</p>
          <button onClick={() => navigate("/")} className="mt-8 border border-black px-6 py-3 mono text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors">
            Return home
          </button>
        </div>
      </Layout>
    );
  }
  if (!post) {
    return (
      <Layout>
        <div className="max-w-[960px] mx-auto px-4 py-24 mono text-sm text-neutral-500">Loading&hellip;</div>
      </Layout>
    );
  }

  const cleanTitle = (post.title || "").replace(/\s*\n+\s*/g, " ");
  const seoDescription = makeMetaDescription(post.summary || post.body);
  const canonicalPath = `/post/${post.id}`;
  const heroImage = (() => {
    const url = post.media_url || "";
    if (!url) return undefined;
    if (/^https?:/i.test(url)) return url;
    if (url.startsWith("/api/uploads/")) return `${SITE_ORIGIN}${url}`;
    return undefined;
  })();
  const schemaType = post.type === "poll" ? "QAPage" : post.type === "forum" ? "DiscussionForumPosting" : "Article";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": schemaType,
    headline: cleanTitle,
    description: seoDescription,
    mainEntityOfPage: `${SITE_ORIGIN}${canonicalPath}`,
    url: `${SITE_ORIGIN}${canonicalPath}`,
    datePublished: post.created_at || undefined,
    dateModified: post.updated_at || post.created_at || undefined,
    image: heroImage ? [heroImage] : undefined,
    author: post.author?.name
      ? { "@type": "Person", name: post.author.name }
      : undefined,
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_ORIGIN}/logo.png`,
      },
    },
  };

  return (
    <Layout>
      <SEO
        path={canonicalPath}
        title={cleanTitle}
        description={seoDescription}
        type={post.type === "essay" || post.type === "interview" ? "article" : "website"}
        image={heroImage || undefined}
        publishedAt={post.created_at}
        updatedAt={post.updated_at || post.created_at}
        authorName={post.author?.name || undefined}
        structuredData={structuredData}
      />
      {(post.type === "essay" || post.type === "interview") && <EssayView post={post} />}
      {post.type === "poll" && post.likert_aggregates && <LikertPollView post={post} onVote={(p) => setPost(p)} />}
      {post.type === "poll" && !post.likert_aggregates && <PollView post={post} onVote={(p) => setPost(p)} />}
      {post.type === "forum" && (
        <ForumView post={post} comments={comments} onCommentAdded={loadComments} />
      )}
    </Layout>
  );
}
